import EmblaCarousel from 'embla-carousel'
import type { EmblaCarouselType } from 'embla-carousel'

interface CategoriesState {
  el: HTMLElement
  slider?: EmblaCarouselType
  activeSlideIndex: number
  availableDirections: {
    prev: boolean
    next: boolean
  }
}

/**
 * Обновляет видимость кнопок навигации
 *
 * @param {CategoriesState} state - Состояние слайдера
 * @param {object} directions - Доступные направления
 */
const updateButtonsVisibility = (
  state: CategoriesState,
  directions: { prev: boolean; next: boolean }
): void => {
  Object.entries(directions).forEach(([key, isAvailable]) => {
    const button = state.el.querySelector<HTMLButtonElement>(
      `[data-slider-button][data-dir="${key}"]`
    )

    if (!button) return

    if (isAvailable) {
      button.classList.remove('categories__slider-button_hidden')
    } else {
      button.classList.add('categories__slider-button_hidden')
    }
  })
}

/**
 * Обработчик изменения активного слайда
 *
 * @param {CategoriesState} state - Состояние слайдера
 * @param {number} newIndex - Новый индекс слайда
 */
const handleSlideChange = (state: CategoriesState, newIndex: number): void => {
  if (newIndex === state.activeSlideIndex || !state.slider) {
    return
  }

  state.slider.scrollTo(newIndex)

  const directions = {
    prev: state.slider.canScrollPrev(),
    next: state.slider.canScrollNext()
  }

  state.availableDirections = directions
  updateButtonsVisibility(state, directions)
}

/**
 * Инициализация слайдера categories
 */
export const initCategories = (): void => {
  const sliderEl = document.querySelector<HTMLElement>('[data-categories]')

  if (!sliderEl) {
    return
  }

  const state = new Proxy<CategoriesState>(
    {
      el: sliderEl,
      // eslint-disable-next-line no-undefined
      slider: undefined,
      activeSlideIndex: 0,
      availableDirections: {
        prev: false,
        next: true
      }
    },
    {
      set(target, prop: keyof CategoriesState, value): boolean {
        if (prop === 'activeSlideIndex' && typeof value === 'number') {
          handleSlideChange(target, value)
        }

        if (
          prop === 'availableDirections' &&
          typeof value === 'object' &&
          'prev' in value &&
          'next' in value
        ) {
          updateButtonsVisibility(target, value)
        }

        target[prop] = value as never

        return true
      }
    }
  )

  // eslint-disable-next-line new-cap
  state.slider = EmblaCarousel(sliderEl, {
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true
  })

  state.slider.on('select', () => {
    if (state.slider) {
      state.activeSlideIndex = state.slider.selectedScrollSnap()
    }
  })

  sliderEl.addEventListener('click', (event: MouseEvent) => {
    const target = event.target as HTMLElement
    const button = target.closest<HTMLButtonElement>('[data-slider-button]')

    if (!button) {
      return
    }

    const delta = button.dataset.dir === 'next' ? 1 : -1
    state.activeSlideIndex += delta
  })
}
